import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { of } from 'rxjs';
import {
  ChapterInfo,
  ColActionId,
  DocumentInfo,
  DocumentUpdateResponse,
  EditorButtonConfiguration,
  EditorEditItem,
  EditorLink,
  IoniconsOutline,
  NavigationData,
  ND,
  PermissionDialog,
  StoreTemplateStatus,
  TemplateGroup
} from '@pt/pt-models';
import { delay } from 'rxjs/operators';
import { CommonDocumentEditorDetails } from '@primatoo-ms-organization';
import {
  LegalEntity
} from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheck/_getglobalmeascheckfordetailsresponse/LegalEntity';
import { EditorStoreService } from '../../services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { AppLinkBuilder } from '../../../utils';

@UntilDestroy()
@Component({
  selector: 'pt-editor-sidebar',
  templateUrl: './editor-sidebar.component.html',
  styleUrls: [`editor-sidebar.component.scss`]
})
export class EditorSidebarComponent implements OnChanges {
  @Input() documentInfo: DocumentInfo;
  /**
   * Attenzione non utilizzarlo se non nell'ONCHANGE usare this.editorInternalStatus.details
   */
  @Input() documentEditorDetails: CommonDocumentEditorDetails;
  @Input() selectedGroup: TemplateGroup;
  @Input() permissionDialog?: PermissionDialog;
  @Input() documentPermission: AvailableOperations;
  /**
   * valorizzare solo quelli che si vogliono far vedere
   */
  @Input() nameButton: EditorButtonConfiguration;
  @Input() internalIdButton: EditorButtonConfiguration;

  @Input() legalEntityButton: EditorButtonConfiguration;
  @Input() legalEntities: LegalEntity[];
  @Input() legalEntitiesNavigationData: NavigationData[];
  @Input() legalEntityId: number;
  @Input() legalEntityLabel: string; // = this.translate.stream('LABELS.LEGAL_ENTITY');
  @Input() legalEntitySelectLabel: string; // = this.translate.stream('TITLE.SELECT_LEGAL_ENTITY');
  @Input() folderButton: EditorButtonConfiguration;
  @Input() foldersNavigationData: NavigationData[];
  @Input() folderId: number;
  @Input() treatmentCategoryButton: EditorButtonConfiguration;
  @Input() treatmentCategoryNavigationData: NavigationData[];
  @Input() treatmentCategoryId: number;

  @Input() departmentButton: EditorButtonConfiguration;
  @Input() departmentNavigationData: NavigationData[];
  @Input() departmentId: number;

  @Input() processOwnerButton: EditorButtonConfiguration;
  @Input() processOwnerNavigationData: NavigationData[];
  @Input() processOwnerId: number;

  @Input() links: EditorLink[];

  @Input() status: StoreTemplateStatus;
  @Input() selectedTemplateGroup: TemplateGroup;
  @Input() previewLoading: boolean;
  @Input() wordLoading: boolean;
  @Output() documentActionEvent = new EventEmitter<ColActionId>();
  @Output() preview = new EventEmitter();
  @Output() downloadWord = new EventEmitter();
  @Output() updateDocument = new EventEmitter<DocumentUpdateResponse>();
  @Output() enabledEditDocument = new EventEmitter<EditorEditItem>();
  EditorEditItem = EditorEditItem;
  IconCheck = IoniconsOutline.check;
  IconWarning = IoniconsOutline.warning;
  ND = ND;
  link?: string;
  chapterInfo?: ChapterInfo;

  constructor(public editorStoreService: EditorStoreService) {
    editorStoreService.chapterInfo$.pipe(untilDestroyed(this))
      .subscribe(chapterInfo => {
        this.chapterInfo = chapterInfo;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.folderId) {
      this.link = this.folderId ? AppLinkBuilder.linkOfWorkspace(this.folderId) : undefined;
    }

  }

  resetField(field: EditorEditItem) {
    this.enabledEditDocument.emit(null);
    switch (field) {
      case EditorEditItem.NAME:
        if (this.documentEditorDetails.name) {
          const name = this.documentEditorDetails.name;
          this.documentEditorDetails.name = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => {
            this.documentEditorDetails.name = name;
          });
        }
        break;
      case EditorEditItem.INTERNAL_ID:
        if (this.documentEditorDetails.internalId) {
          const internalId = this.documentEditorDetails.internalId;
          this.documentEditorDetails.internalId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => {
            this.documentEditorDetails.internalId = internalId;
          });
        }
        break;
      case EditorEditItem.LEGAL_ENTITY:
        if (this.documentEditorDetails.legalEntityId) {
          const legalEntityId = this.documentEditorDetails.legalEntityId;
          this.documentEditorDetails.legalEntityId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => {
            this.documentEditorDetails.legalEntityId = legalEntityId;
          });
        }
        break;
      case EditorEditItem.FOLDER:
        if (this.folderId) {
          const folderId = this.folderId;
          this.folderId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => this.folderId = folderId);
        }
        break;
      case EditorEditItem.TREATMENT_CATEGORY:
        if (this.treatmentCategoryId) {
          const treatmentCategoryId = this.treatmentCategoryId;
          this.treatmentCategoryId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => this.treatmentCategoryId = treatmentCategoryId);
        }
        break;

      case EditorEditItem.DEPARTMENT:
        if (this.departmentId) {
          const departmentId = this.departmentId;
          this.departmentId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => this.departmentId = departmentId);
        }
        break;

      case EditorEditItem.PROCESS_OWNER:
        if (this.processOwnerId) {
          const processOwnerId = this.processOwnerId;
          this.departmentId = null;
          of(true).pipe(
            delay(250)
          ).subscribe(() => this.processOwnerId = processOwnerId);
        }
        break;
    }
  }

  editField(field: EditorEditItem) {
    this.enabledEditDocument.emit(field);
  }

  saveField(field: EditorEditItem, value: string | number) {

    const doc: CommonDocumentEditorDetails = this.documentEditorDetails;
    const request = {
      name: field === EditorEditItem.NAME ? value : doc?.name,
      description: this.documentEditorDetails?.description,
      internalId: field === EditorEditItem.INTERNAL_ID ? value : doc?.internalId,
      legalEntityId: field === EditorEditItem.LEGAL_ENTITY ? value : doc?.legalEntityId,
      parentId: field === EditorEditItem.FOLDER ? value : doc?.parentId,
      processOwnerId: field === EditorEditItem.PROCESS_OWNER ? value : doc?.processOwnerId,
      departmentId: field === EditorEditItem.DEPARTMENT ? value : doc?.departmentId,
      treatmentCategoryId: field === EditorEditItem.TREATMENT_CATEGORY ? value : doc?.treatmentCategoryId,
      latestChange: field
    } as DocumentUpdateResponse;

    this.updateDocument.emit(request);
  }
}
